import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AdminApi from '@/api/adminApi';
import { CaseQuery } from '@/schema/admin';
import { useProfile } from '@/hooks/useProfile';

export const ADMIN_ROLE = 'ADMIN';
export const SUPERADMIN_ROLE = 'SUPERADMIN';

/**
 * The whole admin viewport hangs off this. `isLoading` matters as much as the result:
 * routing on `isAdmin === false` while the profile is still in flight would bounce an
 * admin straight back to the clinic app on every reload.
 *
 * `isAdmin` is the read answer and a superadmin satisfies it — the server grants
 * ROLE_ADMIN alongside ROLE_SUPERADMIN, so a stored role set of just SUPERADMIN still
 * opens the console. `isSuperAdmin` is the write answer: a plain admin is a platform
 * viewer, and every mutating control in the console keys off this instead.
 */
export const useIsAdmin = () => {
  const { data, isLoading, isError } = useProfile();
  const isSuperAdmin = Boolean(data?.roles?.includes(SUPERADMIN_ROLE));
  return {
    isAdmin: isSuperAdmin || Boolean(data?.roles?.includes(ADMIN_ROLE)),
    isSuperAdmin,
    isLoading,
    isError,
    profile: data,
  };
};

export const useAdminOverview = () =>
  useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: () => AdminApi.getOverview(),
  });

export const useAdminClinics = (page: number, size: number, search?: string) =>
  useQuery({
    queryKey: ['admin', 'clinics', page, size, search ?? ''],
    queryFn: () => AdminApi.getClinics(page, size, search),
  });

export const useAdminClinic = (id?: string) =>
  useQuery({
    queryKey: ['admin', 'clinic', id],
    queryFn: () => AdminApi.getClinic(id!),
    enabled: Boolean(id),
  });

export const useAdminCases = (page: number, size: number, query: CaseQuery) =>
  useQuery({
    queryKey: ['admin', 'cases', page, size, query],
    queryFn: () => AdminApi.getCases(page, size, query),
  });

/**
 * Set when production takes the printable models. The board and the clinic-side scan
 * lists both read the stage, so both are refreshed.
 */
export const useSetCasePrinting = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scanId, printing }: { scanId: string; printing: boolean }) =>
      AdminApi.setCasePrinting(scanId, printing),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'cases'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
      queryClient.invalidateQueries({ queryKey: ['jawsScanSummary'] });
      queryClient.invalidateQueries({ queryKey: ['jawsScanSummaries'] });
    },
  });
};

/** Short terms are ignored server-side, so do not even ask. */
export const useAdminSearch = (term: string) =>
  useQuery({
    queryKey: ['admin', 'search', term],
    queryFn: () => AdminApi.search(term),
    enabled: term.trim().length >= 2,
  });

export const useAdminUsers = (page: number, size: number) =>
  useQuery({
    queryKey: ['admin', 'users', page, size],
    queryFn: () => AdminApi.getUsers(page, size),
  });

export const useSetUserEnabled = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      AdminApi.setUserEnabled(id, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
};

export const useSetUserRoles = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) => AdminApi.setUserRoles(id, roles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => AdminApi.deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'audit-log'] });
    },
  });
};

export const useAuditLog = (page: number, size: number, action?: string) =>
  useQuery({
    queryKey: ['admin', 'audit-log', page, size, action ?? ''],
    queryFn: () => AdminApi.getAuditLog(page, size, action),
  });
